import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';

import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { Currency } from '@/data/currencies/type';
import { FF_DASHBOARD_USDC_USDT_WALLET_DETAILS } from '@/data/featureFlags';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { AccountDetailsModal } from '@/Screens/Dashboard/Home/AccountDetailsModal';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';

interface Props {
  readonly balance: DetailedBalance;
  readonly showDetailsButton?: boolean;
  readonly beneficiaryId?: string;
}

export const BalanceWidget: React.FC<Props> = (props: Props): React.ReactElement => {
  const [isDetailsOpen, setIsDetailsOpen] = React.useState<boolean>(false);
  const { balance, showDetailsButton = true, beneficiaryId } = props;

  const formatter = useCurrencyFormatter();
  const formattedBalance = React.useMemo(
    (): string => formatter.format(balance.available, balance.currencyCode),
    [balance.currencyCode, balance.available, formatter],
  );
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  const showUsdcUsdtDetails = useIntegratorFeatureFlag(FF_DASHBOARD_USDC_USDT_WALLET_DETAILS);
  const currency = React.useMemo(
    (): Currency => Currency.fromCode(locale, balance.currencyCode, displayUsdcAsUsd),
    [balance.currencyCode, locale, displayUsdcAsUsd],
  );

  const Icon = React.useMemo(
    (): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => currency.icon,
    [currency.icon],
  );

  const onShowDetails = React.useCallback((): void => {
    setIsDetailsOpen(true);
  }, []);

  const onCloseDetails = React.useCallback((): void => {
    setIsDetailsOpen(false);
  }, []);

  const modalDescription = React.useMemo(
    (): React.ReactElement | undefined =>
      currency.code === CurrencyCodeType.USDC ||
      currency.code === CurrencyCodeType.USDT ? undefined : (
        <Stack direction="row" alignItems="center" gap={1} pt={1}>
          <currency.icon width={24} height={24} />
          <Typography variant="body2" color="text.primary">
            {currency.name}
          </Typography>
        </Stack>
      ),
    [currency],
  );

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      justifyContent="space-between"
      bgcolor="custom.Primary1Background"
      borderRadius={2}
      padding={3}>
      <Box display="flex" alignItems="center" gap={3}>
        <Icon width={32} height="100%" />
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body1" color="custom.LightGray">
            {tr('BALANCE')}
          </Typography>
          <Typography fontSize={24} fontWeight={400} lineHeight="120%" letterSpacing="-1%">
            {formattedBalance}
          </Typography>
        </Box>
      </Box>

      {showDetailsButton &&
        (currency.code === CurrencyCodeType.USD ||
          (showUsdcUsdtDetails &&
            (currency.code === CurrencyCodeType.USDC ||
              currency.code === CurrencyCodeType.USDT))) && (
          <Button variant="light" size="compact" onClick={onShowDetails}>
            {tr('SEE_DETAILS')}
          </Button>
        )}

      <Dialog open={isDetailsOpen} onClose={onCloseDetails}>
        <ApplicationModal
          title={tr('ACCOUNT_DETAILS')}
          description={modalDescription}
          onClose={onCloseDetails}>
          <AccountDetailsModal currency={currency.code} beneficiaryId={beneficiaryId} />
        </ApplicationModal>
      </Dialog>
    </Box>
  );
};
